<template>
    <div v-if="employee_extended && hasAccess">
        <top-nav-bar goto="/?page=profile"></top-nav-bar>

        <div class="py-2 clearfix">
            <h4 class="mb-10 pl-4 text-lg">
                <span class="pb-2 border-b-4 border-blue">
                    {{ trans('crewman::health_insurance.state.health_insurance') }}
                </span>
            </h4>

            <div class="flex flex-col lg:flex-row">
                <div class="flex-1 px-4 pb-0 lg:pb-4">
                    <form-group name="health_insurance.type"
                                :label="trans('crewman::health_insurance.state.what_type_of_insurance')">
                        <v-select :options="formattedHealthInsuranceTypes" v-model="health_insurance.type"></v-select>
                    </form-group>
                </div>

                <div class="flex-1 px-4 pb-0 lg:pb-4">
                    <form-group name="health_insurance.number"
                                :label="trans('crewman::health_insurance.state.health_insurance_number')">
                        <input type="text" class="input" v-model="health_insurance.number">
                    </form-group>
                </div>
            </div>

            <div class="flex flex-col lg:flex-row">
                <div class="flex-1 px-4 pb-0 lg:pb-4">
                    <form-group name="health_insurance.fund"
                                :label="trans('crewman::health_insurance.state.health_insurance_fund')">
                        <v-select :options="formattedHealthInsuranceFunds" v-model="health_insurance.fund"
                                  :on-change="(value) => changeFund(value)"
                        ></v-select>
                    </form-group>
                </div>

                <div class="flex-1 px-4 pb-0 lg:pb-4">
                    <form-group v-if="health_insurance.fund === 'Other'" name="health_insurance.other_fund"
                                :label="trans('crewman::health_insurance.state.other_health_insurance_fund')">
                        <input type="text" class="input" v-model="health_insurance.other_fund">
                    </form-group>
                </div>
            </div>

            <h4 class="mb-10 pl-4 text-lg">
                <span class="pb-2 border-b-4 border-blue">
                    {{ trans('crewman::health_insurance.state.information_about_children') }}
                </span>

                <button @click="addInformationAboutChildren"
                        class="bg-blue absolute mx-2 py-2 px-3 text-white rounded-full">
                    <i class="fal fa-plus"></i>
                </button>
            </h4>

            <div class="flex flex-col lg:flex-row"
                 v-for="(children, index) in health_insurance_children"
                 :key="`health-insurance-children-${index}`">
                <div class="flex-1 px-4 pb-0 lg:pb-4">
                    <form-group :name="`health_insurance_children.${index}.first_name`"
                                :label="trans('crewman::health_insurance.state.first_name')">
                        <input type="text" class="input" v-model="children.first_name">
                    </form-group>
                </div>

                <div class="flex-1 px-4 pb-0 lg:pb-4">
                    <form-group :name="`health_insurance_children.${index}.last_name`"
                                :label="trans('crewman::health_insurance.state.last_name')">
                        <input type="text" class="input" v-model="children.last_name">
                    </form-group>
                </div>

                <div class="flex-1 px-4 pb-0 lg:pb-4">
                    <form-group :name="`health_insurance_children.${index}.date_of_birth`"
                                :label="trans('crewman::health_insurance.state.date_of_birth')">
                        <datepicker v-model="children.date_of_birth" />
                    </form-group>
                </div>

                <div class="h-full flex lg:w-24 lg:pl-4 lg:pt-4 lg:pb-4 pb-8 justify-center items-center">
                    <button @click="removeInformationAboutChildren(index)"
                            class="bg-red mt-1 py-2 px-3 text-white rounded-full">
                        <i class="fal fa-times"></i>
                    </button>
                </div>
            </div>

            <div class="block md:inline-flex md:float-right px-4">
                <button class="btn btn--colored__green w-full" @click.prevent="save">
                    <template v-if="saving">
                        <i class="fal fa-spinner fa-spin"></i>
                        {{ trans('crewman::common.button.saving') }}
                    </template>
                    <template v-else>
                        <i class="fal fa-check"></i>
                        {{ trans('crewman::common.button.save') }}
                    </template>
                </button>
            </div>

        </div>
    </div>
</template>

<script>
import {mapState} from 'vuex';
import CanAccessView from "../mixins/CanAccessView";
import NeedsHealthInsuranceTypes from "../mixins/NeedsHealthInsuranceTypes";
import NeedsHealthInsuranceFunds from "../mixins/NeedsHealthInsuranceFunds";

export default {
    mixins: [CanAccessView, NeedsHealthInsuranceTypes, NeedsHealthInsuranceFunds],

    data() {
        return {
            health_insurance: {
                type: null,
                number: null,
                fund: null,
                other_fund: null
            },
            health_insurance_children: [],
            saving: false,
        }
    },

    created() {
        if (this.employee_extended) {
            if (this.employee_extended.health_insurance) {
                let health_insurance = this.employee_extended.health_insurance;

                if (!health_insurance.fund && !!health_insurance.other_fund) {
                    health_insurance.fund = 'Other';
                }

                this.health_insurance = health_insurance;
            }
            this.health_insurance_children = this.employee_extended.health_insurance_children || [];
        }

        this.$store.dispatch('fetchEmployeeExtended');
    },

    computed: {
        ...mapState(['employee_extended']),

        formattedHealthInsuranceTypes() {
            return this.health_insurance_types.map(el => el.value).sort((a, b) => a > b ? 1 : -1);
        },

        formattedHealthInsuranceFunds() {
            let healthInsuranceFunds = this.health_insurance_funds.map(el => el.value)
                .filter(el => el !== 'Other')
                .sort((a, b) => a > b ? 1 : -1);
            healthInsuranceFunds.push('Other');

            return healthInsuranceFunds;
        },
    },

    methods: {
        changeFund(value) {
            let health_insurance = this.health_insurance;
            if (value === null) {
                health_insurance.other_fund = null;
            }
            health_insurance.fund = value;
            this.health_insurance = health_insurance;
        },

        addInformationAboutChildren() {
            let health_insurance_children = this.health_insurance_children;
            health_insurance_children.push({
                first_name: null,
                last_name: null,
                date_of_birth: null
            });
            this.employee_extended.health_insurance_children = health_insurance_children;
        },

        removeInformationAboutChildren(index) {
            this.employee_extended.health_insurance_children.splice(index, 1);
        },

        save() {
            this.saving = true;

            let health_insurance = this.health_insurance;
            if (health_insurance.fund !== 'Other') {
                health_insurance.other_fund = '';
            }

            this.$api('hr').post('employee/health-insurance', {
                health_insurance: health_insurance,
                health_insurance_children: this.health_insurance_children
            }).then(() => {
                this.$store.dispatch('fetchEmployeeExtended')
                this.$noty.success('Updated health insurance.')
                this.saving = false;
            }).catch(() => {
                this.saving = false;
            })
        }
    },

    watch: {
        'employee_extended.health_insurance': {
            deep: true,
            handler(val) {
                let health_insurance = val;
                if (!health_insurance) {
                    health_insurance = {
                        type: null,
                        number: null,
                        fund: null,
                        other_fund: null
                    };
                }

                if (!health_insurance.fund && !!health_insurance.other_fund) {
                    health_insurance.fund = 'Other';
                }

                this.health_insurance = health_insurance;
            }
        },
        'employee_extended.health_insurance_children': {
            deep: true,
            handler(val) {
                this.health_insurance_children = val || [];
            }
        },
    }
}
</script>
